import React, { useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { TextField, Button, Typography, Container, Box, CssBaseline, Avatar, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Link, IconButton, Snackbar, Alert, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import axios from 'axios';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [consent, setConsent] = useState(false);
    const [open, setOpen] = useState(false);
    const [mobileWarning, setMobileWarning] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (window.innerWidth <= 768) {
            setMobileWarning(true);
        }
    }, []);

    const fetchIPAndLocation = async () => {
        try {
            const response = await axios.get('https://ipapi.co/json/');
            return {
                ip: response.data.ip,
                location: `${response.data.city}, ${response.data.region}, ${response.data.country_name}`
            };
        } catch (error) {
            console.error("Failed to fetch IP and location:", error);
            return { ip: 'N/A', location: 'N/A' };
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        if (!consent) {
            setSnackbar({ open: true, message: 'You must agree to the terms and conditions.', severity: 'warning' });
            return;
        }
        setIsLoading(true);
        const auth = getAuth();
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const { ip, location } = await fetchIPAndLocation();

            const session = {
                uid: user.uid,
                email: user.email,
                time: new Date().toISOString(),
                ip: ip,
                location: location
            };
            await addDoc(collection(db, 'loginSessions'), session);

            if (mobileWarning) {
                setSnackbar({ open: true, message: 'Some features may have limited support on mobile devices.', severity: 'info' });
            }

            navigate('/');
        } catch (error) {
            const message = error.code === 'auth/invalid-credential'
                ? 'Invalid username or password.'
                : error.code === 'auth/too-many-requests'
                ? 'Too many attempts. Please try again later.'
                : 'Unable to sign in. Please try again.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickOpen = (event) => {
        event.preventDefault();
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: '#0a0a0b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Container component="main" maxWidth="sm">
                <CssBaseline />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#1a1a1b',
                        padding: { xs: 3, sm: 4 },
                        borderRadius: '16px',
                        border: '1px solid #3F3F46',
                        animation: 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '@keyframes fadeIn': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(20px) scale(0.98)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0) scale(1)',
                            },
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: '-1px',
                            borderRadius: '16px',
                            padding: '1px',
                            background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            pointerEvents: 'none',
                            opacity: 0.5,
                        },
                    }}
                >
                    <Avatar
                        sx={{
                            m: 1,
                            mb: 2,
                            bgcolor: '#3F3F46',
                            width: 48,
                            height: 48,
                            border: '1px solid #52525B',
                            borderRadius: '14px',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                bgcolor: '#52525B',
                            },
                        }}
                    >
                        <LockOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                    </Avatar>
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{
                            mb: 4,
                            color: '#FFFFFF',
                            fontWeight: 600,
                            textAlign: 'center',
                        }}
                    >
                        Welcome to myHOA
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleLogin}
                        noValidate
                        sx={{
                            mt: 1,
                            width: '100%',
                            '& .MuiTextField-root + .MuiTextField-root': {
                                mt: 2,
                            },
                        }}
                    >
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Username"
                            name="email"
                            autoComplete="off"
                            autoFocus
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#1a1a1b',
                                    '&:hover': {
                                        backgroundColor: '#262626',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#262626',
                                        '& fieldset': {
                                            borderColor: '#FFFFFF',
                                            borderWidth: '1px',
                                        },
                                    },
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="off"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#1a1a1b',
                                    '&:hover': {
                                        backgroundColor: '#262626',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#262626',
                                        '& fieldset': {
                                            borderColor: '#FFFFFF',
                                            borderWidth: '1px',
                                        },
                                    },
                                },
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consent}
                                    onChange={() => setConsent(!consent)}
                                    sx={{
                                        color: '#71717A',
                                        borderRadius: '6px',
                                        '&.Mui-checked': {
                                            color: '#FFFFFF',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: '#A1A1AA' }}>
                                    I agree to these{' '}
                                    <Link
                                        component="button"
                                        onClick={handleClickOpen}
                                        sx={{
                                            color: '#FFFFFF',
                                            textDecoration: 'none',
                                            position: 'relative',
                                            '&:hover': {
                                                textDecoration: 'none',
                                                '&::after': {
                                                    width: '100%',
                                                },
                                            },
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                width: '0%',
                                                height: '1px',
                                                bottom: '-1px',
                                                left: '0',
                                                backgroundColor: '#FFFFFF',
                                                transition: 'width 0.2s ease',
                                            },
                                        }}
                                    >
                                        terms
                                    </Link>
                                </Typography>
                            }
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                                mt: 3,
                                mb: 2,
                                height: '48px',
                                fontSize: '0.9375rem',
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#0a0a0b',
                                boxShadow: 'none',
                                '&.MuiButton-contained': {
                                    color: '#0a0a0b',
                                },
                                '&:hover': {
                                    backgroundColor: '#F4F4F5',
                                    boxShadow: 'none',
                                },
                                '&:disabled': {
                                    backgroundColor: '#A1A1AA',
                                    color: '#0a0a0b',
                                },
                            }}
                        >
                            {isLoading ? <CircularProgress size={22} sx={{ color: '#0a0a0b' }} /> : 'Sign In'}
                        </Button>
                    </Box>
                </Box>
            </Container>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                TransitionProps={{
                    timeout: 300,
                }}
            >
                <DialogTitle
                    sx={{
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    Terms and Conditions
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: '#71717A',
                            '&:hover': {
                                backgroundColor: '#3F3F46',
                                color: '#FFFFFF',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: '24px' }}>
                    <iframe
                        src="/terms.pdf"
                        width="100%"
                        height="600px"
                        style={{
                            border: 'none',
                            borderRadius: '12px',
                            backgroundColor: '#0a0a0b',
                        }}
                        title="Terms and Conditions"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        sx={{
                            color: '#FFFFFF',
                            backgroundColor: '#3F3F46',
                            borderRadius: '10px',
                            padding: '8px 16px',
                            '&:hover': {
                                backgroundColor: '#52525B',
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{
                        backgroundColor: '#1a1a1b',
                        color: '#FFFFFF',
                        border: '1px solid',
                        borderColor: 'grey.700',
                        '& .MuiAlert-icon': {
                            color: snackbar.severity === 'error' ? 'error.main' :
                                   snackbar.severity === 'warning' ? 'warning.main' :
                                   snackbar.severity === 'info' ? 'info.main' : 'success.main'
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Login;
